import React from 'react';
import { Box, Paper, Typography, Button, Chip } from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PhoneIcon from '@mui/icons-material/Phone';
import PeopleIcon from '@mui/icons-material/People';
import CampaignIcon from '@mui/icons-material/Campaign';
import GroupIcon from '@mui/icons-material/Group';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import { BLOCKED_STATUS_TITLE, BLOCKED_STATUS_BODY, BLOCKED_STATUS_HINT } from '../utils/blockedStatus';
import ClientMonitoringDashboard from './monitoring/ClientMonitoringDashboard';

const statusLabels = {
  not_started: 'Not Started', fb_connected: 'FB Connected', pending: 'Pending',
  payment_pending: 'Payment Needed', onboarding: 'Setting Up', details_submitted: 'Details Submitted',
  otp_ready: 'OTP Ready', otp_verified: 'OTP Verified', active: 'Active', suspended: 'Suspended'
};
const statusColors = {
  not_started: '#64748b', fb_connected: '#f59e0b', pending: '#f59e0b',
  payment_pending: '#f59e0b', onboarding: '#3b82f6', details_submitted: '#8b5cf6',
  otp_ready: '#3b82f6', otp_verified: '#22c55e', active: '#22c55e', suspended: '#ef4444'
};

const SectionCard = ({ title, children, sx }) => (
  <Paper className="glass-card" sx={{ p: 3.5, borderRadius: 3, ...sx }}>
    <Typography fontWeight={700} fontSize={15} color="#1e293b" mb={2.5}>{title}</Typography>
    {children}
  </Paper>
);

const LinkItem = ({ href, label }) => (
  <Box component="a" href={href} target="_blank" rel="noopener noreferrer"
    sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, borderRadius: 2, textDecoration: 'none', color: '#475569', bgcolor: 'rgba(7,94,84,0.06)', '&:hover': { bgcolor: 'rgba(7,94,84,0.12)' } }}>
    <OpenInNewIcon sx={{ fontSize: 16, color: '#075e54', flexShrink: 0 }} />
    <Typography variant="body2" fontWeight={500}>{label}</Typography>
  </Box>
);

const DetailRow = ({ label, value, icon, mono }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
    <Typography variant="caption" color="#64748b" fontWeight={600}>{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="body2" color="#1e293b" fontWeight={600} sx={mono ? { fontFamily: "'Courier New', monospace", fontSize: 12 } : {}}>{value}</Typography>
    </Box>
  </Box>
);

const SetupStatus = ({ label, status }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.5 }}>
    <Typography variant="body2" color="#475569">{label}</Typography>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      {status === 'active' ? <CheckCircleIcon sx={{ fontSize: 16, color: '#22c55e' }} /> : status === 'blocked' ? <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} /> : status === 'in_setup' ? <WarningAmberIcon sx={{ fontSize: 16, color: '#f59e0b' }} /> : <CancelIcon sx={{ fontSize: 16, color: '#ef4444' }} />}
      <Typography variant="caption" fontWeight={600} color={status === 'active' ? '#22c55e' : status === 'blocked' ? '#ef4444' : status === 'in_setup' ? '#f59e0b' : '#ef4444'}>
        {status === 'active' ? 'Completed' : status === 'blocked' ? 'Blocked' : status === 'in_setup' ? 'In Progress' : 'Not Set'}
      </Typography>
    </Box>
  </Box>
);

const PricingRow = ({ icon, label, detail, free }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1.5, borderRadius: 2, bgcolor: free ? 'rgba(34,197,94,0.06)' : 'rgba(245,158,11,0.06)' }}>
    <Box sx={{ width: 40, height: 40, borderRadius: '10px', bgcolor: free ? 'rgba(34,197,94,0.12)' : 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
      {icon}
    </Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="body2" fontWeight={600} color="#1e293b">{label}</Typography>
      <Typography variant="caption" color="#64748b">{detail}</Typography>
    </Box>
    <Chip label={free ? 'Free' : 'Paid'} size="small"
      sx={{ fontWeight: 700, fontSize: 11, borderRadius: '6px', color: '#fff', bgcolor: free ? '#22c55e' : '#f59e0b' }} />
  </Box>
);

const DashboardHome = ({ account, onNavigateCampaigns }) => {
  const isActive = account.onboardingStatus === 'active';
  const needsPayment = account.onboardingStatus === 'payment_pending' || !account.paymentStatus;
  const hasPhoneNumber = !!account.phoneNumberId;
  const hasWaba = !!account.wabaId || !!account.clientWabaId;
  const isApiSetup = hasPhoneNumber && hasWaba && account.webhookSubscribed;
  const blocked = account.apiStatus === 'blocked';
  const accountStatus = blocked ? 'blocked' : isActive ? 'active' : account.onboardingStatus === 'suspended' ? 'suspended' : 'in_setup';
  const conversationCount = account.conversationCount || 0;
  const campaignCount = account.campaignCount || 0;

  return (
    <Box sx={{ p: 4, maxHeight: 'calc(100vh - 64px)', overflow: 'auto' }}>
      <Box sx={{ maxWidth: 960, mx: 'auto', display: 'flex', flexDirection: 'column', gap: '27px' }}>
        <Typography fontWeight={800} fontSize={24} color="#1e293b">Dashboard</Typography>

        {blocked && (
          <Paper className="glass-card" sx={{ p: 3.5, borderRadius: 3, border: '1px solid #ef4444', bgcolor: 'rgba(239,68,68,0.08)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <CancelIcon sx={{ color: '#ef4444', fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} fontSize={14} color="#b91c1c">{BLOCKED_STATUS_TITLE}</Typography>
              <Typography variant="body2" color="#dc2626">{BLOCKED_STATUS_BODY}</Typography>
              <Typography variant="caption" color="#b91c1c" sx={{ mt: 0.5, display: 'block' }}>{BLOCKED_STATUS_HINT}</Typography>
            </Box>
          </Paper>
        )}

        {needsPayment && (
          <Paper className="glass-card" sx={{ p: 3.5, borderRadius: 3, border: '1px solid #f59e0b', bgcolor: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 28 }} />
            <Box sx={{ flex: 1 }}>
              <Typography fontWeight={700} fontSize={14} color="#92400e">Payment Method Required</Typography>
              <Typography variant="body2" color="#a16207">Add a payment method in Meta to send business-initiated messages (campaigns, templates, proactive messages). Without this, only inbound replies within 24hr are allowed.</Typography>
            </Box>
            <Button variant="contained" size="small" href="https://business.facebook.com/wa/manage/payment" target="_blank" rel="noopener noreferrer" startIcon={<OpenInNewIcon />} sx={{ bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' }, textTransform: 'none', borderRadius: 1.5, fontSize: 12, whiteSpace: 'nowrap' }}>
              Add Payment
            </Button>
          </Paper>
        )}

        {!isActive && !needsPayment && (
          <Paper className="glass-card" sx={{ p: 3.5, borderRadius: 3, border: '1px solid #3b82f6', bgcolor: 'rgba(59,130,246,0.08)', display: 'flex', alignItems: 'center', gap: 2 }}>
            <CheckCircleIcon sx={{ color: '#3b82f6', fontSize: 28 }} />
            <Box>
              <Typography fontWeight={700} fontSize={14} color="#1e40af">Setup In Progress</Typography>
              <Typography variant="body2" color="#2563eb">Onboarding status: {statusLabels[account.onboardingStatus]}</Typography>
            </Box>
          </Paper>
        )}

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr 1fr' }, gap: '27px' }}>
          <Paper className="card-hover glass-card" sx={{ p: 3.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: 'rgba(7,94,84,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <PeopleIcon sx={{ color: '#075e54', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748b" fontWeight={600}>Contacts</Typography>
              <Typography fontWeight={800} fontSize={22} color="#1e293b">{conversationCount}</Typography>
            </Box>
          </Paper>
          <Paper className="card-hover glass-card" sx={{ p: 3.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: 'rgba(16,185,129,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <GroupIcon sx={{ color: '#10b981', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748b" fontWeight={600}>Groups</Typography>
              <Typography fontWeight={800} fontSize={22} color="#1e293b">0</Typography>
            </Box>
          </Paper>
          <Paper className="card-hover glass-card" sx={{ p: 3.5, borderRadius: 3, display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ width: 48, height: 48, borderRadius: '14px', bgcolor: 'rgba(245,158,11,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <CampaignIcon sx={{ color: '#f59e0b', fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="caption" color="#64748b" fontWeight={600}>Campaigns</Typography>
              <Typography fontWeight={800} fontSize={22} color="#1e293b">{campaignCount}</Typography>
            </Box>
          </Paper>
        </Box>

        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' }, gap: '27px' }}>
          <SectionCard title="Account Details">
            <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
              <DetailRow label="WhatsApp Number" value={account.whatsappBusinessNumber || account.businessPhone || 'Not set'} icon={<PhoneIcon sx={{ fontSize: 16, color: '#075e54' }} />} />
              <DetailRow label="Business Name" value={account.businessName || 'Not set'} />
              <DetailRow label="WABA ID" value={account.clientWabaId || account.wabaId || 'Not set'} mono />
              <DetailRow label="Phone Number ID" value={account.phoneNumberId || 'Not set'} mono />
              <DetailRow label="Business Manager ID" value={account.facebookBusinessManagerId || 'Not set'} mono />
              <DetailRow label="Payment Status" value={account.paymentStatus || 'Not set'} />
              <DetailRow label="Plan" value={account.planName || 'No plan'} />
              <DetailRow label="Joined" value={account.createdAt ? new Date(account.createdAt).toLocaleDateString() : '—'} />
            </Box>
          </SectionCard>

          <SectionCard title="API Setup">
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <SetupStatus label="Account Status" status={accountStatus} />
              <SetupStatus label="Phone Number Connected" status={hasPhoneNumber ? 'active' : 'inactive'} />
              <SetupStatus label="WABA Connected" status={hasWaba ? 'active' : 'inactive'} />
              <SetupStatus label="Webhook Subscribed" status={account.webhookSubscribed ? 'active' : 'inactive'} />
              <SetupStatus label="API Setup Complete" status={isApiSetup ? 'active' : 'inactive'} />
            </Box>
          </SectionCard>
        </Box>

        <ClientMonitoringDashboard />

        <SectionCard title="Campaigns">
          {campaignCount === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CampaignIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 2 }} />
              <Typography variant="h6" color="#1e293b" fontWeight={700} gutterBottom>No campaigns yet</Typography>
              <Typography color="#64748b" variant="body2" mb={3}>Create your first WhatsApp campaign to start reaching your customers with template messages.</Typography>
              <Button variant="contained" onClick={onNavigateCampaigns} startIcon={<CampaignIcon />}
                sx={{ borderRadius: 2, px: 4 }}>
                Create Campaign
              </Button>
            </Box>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CampaignIcon sx={{ fontSize: 48, color: '#075e54', mb: 2 }} />
              <Typography variant="body2" color="#64748b" mb={2}>You have {campaignCount} campaign(s)</Typography>
              <Button variant="contained" onClick={onNavigateCampaigns} startIcon={<CampaignIcon />}
                sx={{ borderRadius: 2 }}>
                View Campaigns
              </Button>
            </Box>
          )}
        </SectionCard>

        <SectionCard title="Understanding WhatsApp Pricing">
          <Typography variant="body2" color="#64748b" sx={{ mb: 2.5 }}>
            Meta charges for business-initiated conversations on WhatsApp. Here's how it works:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <PricingRow icon={<InfoOutlinedIcon sx={{ color: '#22c55e', fontSize: 20 }} />}
              label="Customer messages you → you reply (within 24hr)"
              detail="Service conversations started by the customer are always free"
              free />
            <PricingRow icon={<InfoOutlinedIcon sx={{ color: '#22c55e', fontSize: 20 }} />}
              label="First 1,000 service conversations / month"
              detail="Meta gives 1,000 free service conversations every month"
              free />
            <PricingRow icon={<CurrencyRupeeIcon sx={{ color: '#f59e0b', fontSize: 20 }} />}
              label="You send first message (template / campaign)"
              detail="Marketing: ~₹0.87–1.20/msg | Utility: ~₹0.12–0.20/msg | Authentication: ~₹0.12–0.20/msg"
              free={false} />
            <PricingRow icon={<CurrencyRupeeIcon sx={{ color: '#f59e0b', fontSize: 20 }} />}
              label="Beyond 1,000 service conversations / month"
              detail="Charged at marketing rates once free limit is crossed"
              free={false} />
          </Box>
          <Box sx={{ mt: 2.5, p: 2, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.08)', display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WarningAmberIcon sx={{ color: '#f59e0b', fontSize: 20 }} />
            <Typography variant="caption" color="#92400e">
              <strong>Important:</strong> Meta requires a payment method to send any business-initiated message. Add your credit card in Meta Business Manager to unlock campaigns, templates, and proactive messaging.
            </Typography>
          </Box>
        </SectionCard>

        <SectionCard title="Important Links">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <LinkItem href="https://business.facebook.com/wa/manage/payment" label="WhatsApp Messages Payment (direct to Facebook)" />
            <LinkItem href="https://business.facebook.com/wa/manage/accounts" label="WhatsApp Business Manager" />
            <LinkItem href={`https://business.facebook.com/wa/manage/message-templates?waba_id=${account.wabaId || account.clientWabaId || ''}`} label="Create WhatsApp Template" />
            <LinkItem href="https://business.facebook.com/wa/manage/phone-numbers" label="Green Tick Tutorial" />
            <LinkItem href="https://business.facebook.com/wa/manage/billing" label="WhatsApp Billing & Invoice" />
            <LinkItem href="https://business.facebook.com/wa/help" label="WhatsApp Direct Support" />
            <LinkItem href={`https://business.facebook.com/wa/manage/accounts?waba_id=${account.wabaId || account.clientWabaId || ''}`} label="WhatsApp Linked Accounts" />
          </Box>
        </SectionCard>
      </Box>
    </Box>
  );
};

export default DashboardHome;
